/**
 * Note Form Modal Component
 * Modal for creating or editing a note
 */

import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { invoiceNotesApi } from '../../../../../services/api/invoiceNotesApi';
import { toast } from 'sonner';

const NoteFormModal = ({ show, onHide, companyId, invoiceId, note = null, onSaved }) => {
  const [noteText, setNoteText] = useState('');
  const [noteType, setNoteType] = useState('internal');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isEdit = !!note;

  // Initialize form with note data when editing
  useEffect(() => {
    if (note) {
      setNoteText(note.note_text || '');
      setNoteType(note.note_type || 'internal');
    } else {
      setNoteText('');
      setNoteType('internal');
    }
    setError(null);
  }, [note, show]);

  const handleSave = async () => {
    // Validate
    if (!noteText.trim()) {
      setError('Anteckningstext krävs');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const noteData = {
        note_text: noteText.trim(),
        note_type: noteType
      };

      if (isEdit) {
        await invoiceNotesApi.update(companyId, invoiceId, note.id, noteData);
      } else {
        await invoiceNotesApi.create(companyId, invoiceId, noteData);
      }

      toast.success(isEdit ? 'Uppdaterad' : 'Skapad', {
        description: isEdit ? 'Anteckningen har uppdaterats' : 'Anteckningen har skapats'
      });

      onSaved();
    } catch (err) {
      console.error('Error saving note:', err);
      const errorMessage = err.response?.data?.message || 'Kunde inte spara anteckningen';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setNoteText('');
      setNoteType('internal');
      setError(null);
      onHide();
    }
  };

  const isSystemNote = note?.note_type === 'system';

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Redigera anteckning' : 'Ny anteckning'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" className="mb-3">{error}</Alert>
        )}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Typ av anteckning</Form.Label>
            <Form.Select
              value={noteType}
              onChange={(e) => setNoteType(e.target.value)}
              disabled={isSystemNote}
            >
              <option value="internal">
                Intern (Privat - Visas inte för kunden)
              </option>
              <option value="external">
                Extern (Kan visas för kunden)
              </option>
            </Form.Select>
            <Form.Text className="text-muted">
              {noteType === 'internal'
                ? 'Interna anteckningar är privata och syns bara för dig och dina teammedlemmar.'
                : 'Externa anteckningar kan vara synliga för kunden beroende på inställningar.'}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Anteckningstext <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={6}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              placeholder="Skriv din anteckning här..."
              required
              disabled={isSystemNote}
            />
          </Form.Group>

          {isSystemNote && (
            <Alert variant="info">
              Detta är en systemgenererad anteckning och kan inte redigeras.
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Avbryt
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={saving || !noteText.trim() || isSystemNote}
        >
          {saving ? 'Sparar...' : (isEdit ? 'Uppdatera' : 'Skapa')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NoteFormModal;
